import React, { useEffect, useState } from "react";
import { useLabAccess } from "../../contexts/LabAccessContext";
import { useDataProvider } from "../../hooks/data/DataProvider";
import "./Projects.css";

const ProjectSelect = ({
    labId,
    gameId,
    ideType,
    level,
    onBack
}) => {

    const { openProject } = useLabAccess();

    const {
        projects,
        fetchProjects,
        addData,
        loading: dataLoading,
        error: dataError
    } = useDataProvider();

    const [filteredProjects, setFilteredProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");


    // ============================================================
    // FILTER PROJECTS
    // ============================================================

    useEffect(() => {

        const filterProjects = () => {

            if (!Array.isArray(projects)) {
                setFilteredProjects([]);
                return;
            }

            const filtered = projects.filter(project => {

                if (
                    labId != null &&
                    String(project.lab_id) !== String(labId)
                ) {
                    return false;
                }

                if (
                    gameId != null &&
                    String(project.game_id) !== String(gameId)
                ) {
                    return false;
                }

                if (
                    ideType &&
                    project.ide_type &&
                    project.ide_type !== ideType
                ) {
                    return false;
                }

                if (
                    level &&
                    project.level &&
                    project.level !== level
                ) {
                    return false;
                }

                return true;
            });

            setFilteredProjects(filtered);

        };

        filterProjects();

    }, [
        projects,
        labId,
        gameId,
        ideType,
        level
    ]);


    // ============================================================
    // LOAD PROJECTS
    // ============================================================

    useEffect(() => {

        let mounted = true;

        const loadProjects = async () => {

            try {

                setLoading(true);
                setError("");

                /*
                 * DataProvider normally loads projects during
                 * its initial load. Calling fetchProjects here
                 * also makes this component safe if the cache
                 * has not been populated yet.
                 */
                if (
                    !Array.isArray(projects) ||
                    projects.length === 0
                ) {
                    await fetchProjects();
                }

            } catch (err) {

                console.error(
                    "ProjectSelect load error:",
                    err
                );

                if (mounted) {
                    setError(
                        err.message ||
                        "Unable to load projects."
                    );
                }

            } finally {

                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadProjects();

        return () => {
            mounted = false;
        };

    }, [fetchProjects]);


    // ============================================================
    // DATA PROVIDER ERROR
    // ============================================================

    useEffect(() => {

        if (dataError) {
            setError(dataError);
        }

    }, [dataError]);


    // ============================================================
    // CREATE PROJECT
    // ============================================================

    const createProject = async () => {

        const name = window.prompt(
            "Enter your project name:"
        );

        if (!name?.trim()) {
            return;
        }

        try {

            setError("");

            /*
             * gameId should be supplied by the game-selection
             * step.
             */
            if (!gameId) {
                throw new Error(
                    "Game ID is required to create a project."
                );
            }

            const project = await addData({

                lab_id: labId,

                game_id: gameId,

                name: name.trim(),

                ide_type: ideType,

                level: level || null
            });

            openProject(
                project.id,
                project.lab_id,
                project.ide_type,
                project.level
            );

        } catch (err) {

            console.error(
                "ProjectSelect create error:",
                err
            );

            setError(
                err.message ||
                "Unable to create project."
            );
        }
    };


    // ============================================================
    // OPEN PROJECT
    // ============================================================

    const handleOpenProject = (project) => {

        openProject(
            project.id,
            project.lab_id,
            project.ide_type,
            project.level
        );
    };


    // ============================================================
    // LOADING
    // ============================================================

    const isLoading =
        loading ||
        dataLoading;


    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="projects-screen">

            <div className="projects-topbar">

                <button
                    type="button"
                    className="back-button"
                    onClick={onBack}
                >
                    ← Back
                </button>

            </div>


            <div className="projects-header">

                <h1>
                    Select Your Project
                </h1>

                <p>
                    Choose an existing project or
                    create a new one.
                </p>

                <div className="project-selection-info">

                    <span>
                        IDE: {ideType}
                    </span>

                    {level && (
                        <span>
                            Level: {level}
                        </span>
                    )}

                </div>

            </div>


            {error && (
                <div className="projects-error">
                    {error}
                </div>
            )}


            {isLoading ? (

                <div className="projects-loading">
                    Loading your projects...
                </div>

            ) : (

                <div className="student-project-grid">

                    {filteredProjects.map(project => (

                        <button
                            key={project.id}
                            type="button"
                            className="student-project-card"
                            onClick={() =>
                                handleOpenProject(project)
                            }
                        >

                            <div className="student-project-main">

                                <span className="project-status">
                                    {project.status || "Active"}
                                </span>

                                <h2>
                                    {project.name ||
                                        project.title ||
                                        "Untitled Project"}
                                </h2>

                                <p>
                                    {project.game_name ||
                                        project.game?.name ||
                                        "STEMX365 Project"}
                                </p>

                            </div>

                            <div className="student-project-arrow">
                                →
                            </div>

                        </button>

                    ))}


                    <button
                        type="button"
                        className="student-project-card new-project-card"
                        onClick={createProject}
                    >

                        <div className="student-project-main">

                            <span className="project-status">
                                NEW
                            </span>

                            <h2>
                                + New Project
                            </h2>

                            <p>
                                Create a new project
                            </p>

                        </div>

                        <div className="student-project-arrow">
                            →
                        </div>

                    </button>

                </div>
            )}

        </div>
    );
};

export default ProjectSelect;